import { FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Fragment } from "react/jsx-runtime";

import { useUpdatePartDimensions } from "#modules/parts/view/useUpdatePartDimensions";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { Flex } from "#shared/view/components/Flex";
import { Modal } from "#shared/view/components/Modal";
import { Stack } from "#shared/view/components/Stack";
import { Button } from "#shared/view/designSystem/Button";
import { NumberInput } from "#shared/view/designSystem/inputs/NumberInput";
import {
  FieldConfig,
  FormatterMaterialParameter,
  getMaterialDimensionsFieldsConfiguration,
  Part,
} from "shared";

type Props = {
  part: (Omit<Partial<Part>, "id"> & { id: string }) | null | undefined;
  quoteRequestLine: Omit<QuoteRequestLineDto, "latestPredictionJob">;
  partDimensionsModalOpened: boolean;
  closePartDimensionsModal: () => void;
  onSubmit?: () => void;
};

const extractData = (formData: FormData, dimension: string) => {
  // oxlint-disable-next-line typescript/no-base-to-string
  const value = formData.get(dimension)?.toString().replace(",", ".");

  return value ? parseFloat(value) : undefined;
};

const getDimensionsFromFormData = (formData: FormData, fieldsConfig: FieldConfig[]) => {
  const dimensions: Record<string, number | undefined> = {};

  fieldsConfig.forEach((field) => {
    const value = extractData(formData, field.field);
    dimensions[field.field] = value;
    if (field.syncWith) {
      dimensions[field.syncWith] = value;
    }
  });

  return dimensions;
};

export const PartDimensionsModal = ({
  part,
  quoteRequestLine,
  partDimensionsModalOpened,
  closePartDimensionsModal,
  onSubmit,
}: Props) => {
  const { t } = useTranslation();

  const [fieldValues, setFieldValues] = useState<
    Record<keyof FormatterMaterialParameter, number | undefined>
  >({
    height: undefined,
    width: undefined,
    length: undefined,
    tubeThickness: undefined,
  });

  const fieldsConfig = useMemo(
    () =>
      getMaterialDimensionsFieldsConfiguration(
        part?.type ?? quoteRequestLine.part.type ?? undefined,
      ),
    [part?.type, quoteRequestLine.part.type],
  );

  const { updatePartDimensionsAsync, isPending } = useUpdatePartDimensions({
    partId: part?.id || "",
    quoteRequestId: quoteRequestLine.quoteRequestId,
    quoteRequestLine,
    layoutId: null,
    options: {
      onSuccess: () => {
        onSubmit?.();
      },
    },
  });

  if (!part || !part.id) {
    return null;
  }

  const handleCancel = () => {
    closePartDimensionsModal();
  };

  const handleValidate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const dimensions = getDimensionsFromFormData(formData, fieldsConfig);

    await updatePartDimensionsAsync({
      id: part.id,
      data: {
        ...dimensions,
        quoteRequestLineId: quoteRequestLine.id,
      },
    });
    closePartDimensionsModal();
  };

  const renderField = (fieldConfig: FieldConfig) => {
    const partValue = part[fieldConfig.field as keyof typeof part];
    const displayValue =
      fieldValues[fieldConfig.field] ??
      (typeof partValue === "string" || typeof partValue === "number" ? partValue : "");

    const handleChange = (value: number) => {
      const newValues = { ...fieldValues, [fieldConfig.field]: value };

      if (fieldConfig.syncWith) {
        newValues[fieldConfig.syncWith] = value;
      }

      setFieldValues(newValues);
    };

    return (
      <NumberInput
        key={fieldConfig.key}
        unit="mm"
        label={t(`material.${fieldConfig.key}`)}
        flex="1"
        name={fieldConfig.field}
        value={displayValue}
        onChange={handleChange}
        min={0}
      />
    );
  };

  return (
    <Fragment>
      <Modal
        opened={partDimensionsModalOpened}
        withCloseButton={false}
        onClose={closePartDimensionsModal}
        size={"50%"}
        title="Dimensions de la pièce"
      >
        <form onSubmit={handleValidate}>
          <Stack gap="sm">
            <Flex gap="xs" justify="end">
              {fieldsConfig.map(renderField)}
            </Flex>

            <Flex gap="xs" justify="end">
              <Button variant="outline" onClick={handleCancel}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={isPending}>
                {t("common.validate")}
              </Button>
            </Flex>
          </Stack>
        </form>
      </Modal>
    </Fragment>
  );
};
